// Same rules as `metabase.ias.param-ranges.core` on the backend. A range token
// `<prefix><from>:<prefix><to>` (same prefix, ending with a letter) becomes
// every value it covers. Keep both sides and their test tables in step.

export const MAX_RANGE_VALUES = 2000;

const RANGE_PATTERN =
  /^([a-z0-9]*[a-z])(\d{1,5})\s*:\s*([a-z0-9]*[a-z])(\d{1,5})$/i;

type RangeToken = {
  prefix: string;
  from: number;
  to: number;
  width: number;
};

function parseRange(value: unknown): RangeToken | null {
  if (typeof value !== "string") {
    return null;
  }
  const groups = RANGE_PATTERN.exec(value.trim());
  if (groups == null) {
    return null;
  }
  const [, prefixA, digitsA, prefixB, digitsB] = groups;
  if (prefixA.toLowerCase() !== prefixB.toLowerCase()) {
    return null;
  }
  const [lowDigits, highDigits] =
    Number(digitsA) <= Number(digitsB)
      ? [digitsA, digitsB]
      : [digitsB, digitsA];
  const isZeroPadded = lowDigits.length > 1 && lowDigits.startsWith("0");
  return {
    prefix: prefixA,
    from: Number(lowDigits),
    to: Number(highDigits),
    width: isZeroPadded ? lowDigits.length : 0,
  };
}

function rangeSize({ from, to }: RangeToken) {
  return to - from + 1;
}

function rangeValues({ prefix, from, to, width }: RangeToken): string[] {
  return Array.from(
    { length: to - from + 1 },
    (_, index) => `${prefix}${String(from + index).padStart(width, "0")}`,
  );
}

// Returns `values` itself when it holds no range token, and `null` when the
// expanded list would exceed `MAX_RANGE_VALUES` (the backend reports that error).
export function expandRangeValues<T>(
  values: readonly T[],
): readonly (T | string)[] | null {
  const ranges = new Map<T, RangeToken>();
  values.forEach((value) => {
    const range = parseRange(value);
    if (range != null) {
      ranges.set(value, range);
    }
  });
  if (ranges.size === 0) {
    return values;
  }

  const rangeTokenCount = values.filter((value) => ranges.has(value)).length;
  const totalSize =
    values.length -
    rangeTokenCount +
    [...ranges.values()].reduce((sum, range) => sum + rangeSize(range), 0);
  if (totalSize > MAX_RANGE_VALUES) {
    return null;
  }

  const expanded = values.flatMap((value): (T | string)[] => {
    const range = ranges.get(value);
    return range != null ? rangeValues(range) : [value];
  });
  return [...new Set(expanded)];
}
